/**
 * Whether an employee has the balance to apply for a leave type.
 *
 * The Apply form showed the balance and then let any request through — "Casual Leave: 0 day(s)"
 * sat above a Submit button that filed five more days of it. Nothing downstream refused it
 * either: the leave-validation Lambda deliberately rules only on the same-day credit question
 * and does not re-implement accrual. So the check lives here, against the same snapshot the
 * balance card shows, which keeps the number on screen and the number enforced identical.
 *
 * Pure: no fetching, no clock. The page passes in what it already holds.
 */

import { LeaveRequest } from '../types/leave';
import { LeaveBalanceSnapshot } from '../types/leavePolicy';
import { leaveDaysFor } from './leaveDuration';

/** Statuses that already count against the balance — see computeSnapshotsFor. */
const COUNTED_STATUSES = new Set(['approved', 'cancellation requested', 'submitted', 'pending']);

/**
 * Days still available for one leave type, or null when the type has no tracked balance
 * (unpaid or unaccrued types, which this rule leaves alone).
 *
 * When EDITING a request of the same type, its own days are given back first: the balance
 * already has them deducted (as pending or approved), and without this an employee with
 * exactly the days they had booked could not move them to another date.
 */
export function availableDays(
  snapshot: LeaveBalanceSnapshot | null | undefined,
  editing?: LeaveRequest | null,
  editingCode?: string
): number | null {
  if (!snapshot) return null;
  if (snapshot.dojMissing) return 0;
  let available = snapshot.balance;
  if (
    editing &&
    editingCode &&
    editingCode.toUpperCase() === snapshot.leaveTypeCode.toUpperCase() &&
    COUNTED_STATUSES.has(String(editing.status).toLowerCase())
  ) {
    available += leaveDaysFor(editing);
  }
  return Math.max(0, Math.round(available * 2) / 2);
}

/**
 * Why a request cannot be submitted against its balance, or undefined when it can.
 *
 * Refuses a type with nothing left, and a request for more days than are left. A type with no
 * tracked balance (available === null) is never refused here.
 */
export function balanceProblem(
  typeLabel: string,
  available: number | null,
  requestedDays: number,
  dojMissing = false
): string | undefined {
  if (available === null || requestedDays <= 0) return undefined;
  if (dojMissing) {
    return `Your date of joining is not set, so you have no ${typeLabel} balance yet. Please contact HR.`;
  }
  if (available <= 0) {
    return `You have no ${typeLabel} balance left, so you cannot apply for it.`;
  }
  if (requestedDays > available) {
    return (
      `You have only ${available} day${available === 1 ? '' : 's'} of ${typeLabel} left, ` +
      `but this request is for ${requestedDays}.`
    );
  }
  return undefined;
}
